/**
 * Map API notification type / referenceModel to NotificationItem icon key.
 */
export function getNotificationIconType(notification) {
  const { type, referenceModel } = notification || {};
  const t = (type || '').toLowerCase();
  if (
    t.includes('ticket') ||
    referenceModel === 'Ticket' ||
    referenceModel === 'TicketMessage'
  ) {
    return 'ticket';
  }
  if (
    t.includes('chat') ||
    t.includes('message') ||
    referenceModel === 'Room' ||
    referenceModel === 'Message'
  ) {
    return 'conversation';
  }
  if (
    t.includes('knowledge') ||
    t.includes('article') ||
    t.includes('kb_feedback') ||
    referenceModel === 'KBFeedbackQueue'
  ) {
    return 'knowledge';
  }
  if (t.includes('document')) return 'document';
  return 'conversation';
}

export function getNotificationVisualLead(notification) {
  const n = notification || {};
  const type = (n.type || '').toLowerCase();
  const m = n.metadata || {};

  // Ticket: assignment / mention → assigner or customer as avatar
  if (type === 'ticket_assigned' && m.assignedBy) {
    return { kind: 'avatar', name: String(m.assignedBy) };
  }
  if (type === 'ticket_message' && m.customerName) {
    return { kind: 'avatar', name: String(m.customerName) };
  }
  if (type === 'new_ticket') {
    return { kind: 'icon', iconType: 'ticket' };
  }
  if (type === 'ticket_status_changed') {
    return { kind: 'icon', iconType: 'ticket' };
  }

  if (type === 'new_chat' && m.visitorName) {
    return { kind: 'avatar', name: String(m.visitorName) };
  }
  if (type === 'chat_message') {
    return { kind: 'icon', iconType: 'conversation' };
  }
  if (type === 'chat_assigned' && (m.assignedBy || m.agentName)) {
    return {
      kind: 'avatar',
      name: String(m.assignedBy || m.agentName),
    };
  }
  if (type === 'chat_ended') {
    return { kind: 'icon', iconType: 'conversation' };
  }

  if (type === 'kb_feedback_received' && m.submittedBy) {
    return { kind: 'avatar', name: String(m.submittedBy) };
  }

  if (type === 'kb_article_pending_review') {
    return { kind: 'icon', iconType: 'knowledge' };
  }

  return { kind: 'icon', iconType: getNotificationIconType(n) };
}

/** Section keys → label as in Figma */
export const NOTIFICATION_SECTION_LABELS = {
  new: 'New',
  today: 'Today',
  yesterday: 'Yesterday',
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function formatDateKey(dayStartMs) {
  const d = new Date(dayStartMs);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Section heading for read items older than yesterday (e.g. "Mar 17" or "Mar 17, 2025"). */
function formatOlderSectionLabel(dayStartMs) {
  const d = new Date(dayStartMs);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function groupNotificationsForInbox(items) {
  const list = Array.isArray(items) ? [...items] : [];
  list.sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
  );

  const now = new Date();
  const todayT = startOfDay(now);
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  const yesterdayT = startOfDay(yest);

  const unread = [];
  const today = [];
  const yesterday = [];
  const olderByDay = new Map();

  for (const n of list) {
    if (!n.isRead) {
      unread.push(n);
    }
  }
  for (const n of list) {
    if (n.isRead) {
      const ct = new Date(n.createdAt || 0).getTime();
      if (ct >= todayT) {
        today.push(n);
      } else if (ct >= yesterdayT && ct < todayT) {
        yesterday.push(n);
      } else if (ct < yesterdayT) {
        const dayStartMs = startOfDay(n.createdAt || 0);
        const dateKey = formatDateKey(dayStartMs);
        let bucket = olderByDay.get(dateKey);
        if (!bucket) {
          bucket = { dayStartMs, items: [] };
          olderByDay.set(dateKey, bucket);
        }
        bucket.items.push(n);
      }
    }
  }

  const olderSections = Array.from(olderByDay.entries())
    .sort(([, a], [, b]) => b.dayStartMs - a.dayStartMs)
    .map(([dateKey, { dayStartMs, items: dayItems }]) => ({
      dateKey,
      label: formatOlderSectionLabel(dayStartMs),
      items: dayItems,
    }));

  return { new: unread, today, yesterday, olderSections };
}

/**
 * Display strings: prefer API `title` / `body`; trim whitespace.
 */
export function getNotificationDisplayText(notification) {
  const n = notification || {};
  const title = (n.title != null ? String(n.title) : '').trim();
  const body = (n.body != null ? String(n.body) : '').trim();
  return { title, body };
}
